import React from "react";
import {
  estimatedLiquidationPrice,
  stopLossPastLiquidationError,
  tpSlTriggerPrice,
} from "@crypto-trading/common/browser";

interface TpSlTargetPriceProps {
  variant: "tp" | "sl";
  percentStr: string;
  /** Current price; the exchange sets TP/SL from the actual fill. */
  entryPrice?: number | null;
  leverage: number;
  positionType: "LONG" | "SHORT";
}

const formatTargetPrice = (price: number): string => {
  if (price >= 1) {
    return price.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: price >= 1000 ? 2 : 4,
    });
  }
  return Number(price.toPrecision(4)).toString();
};

/**
 * Shows the price the token has to reach for a TP/SL to fire, and for SL the
 * estimated liquidation price, so the user sees which one comes first.
 */
export const TpSlTargetPrice: React.FC<TpSlTargetPriceProps> = ({
  variant,
  percentStr,
  entryPrice,
  leverage,
  positionType,
}) => {
  if (!entryPrice || entryPrice <= 0) return null;

  const percent = Number(percentStr);
  const target = tpSlTriggerPrice(entryPrice, percent, positionType, variant);
  const liquidation =
    variant === "sl"
      ? estimatedLiquidationPrice(entryPrice, leverage, positionType)
      : null;
  const pastLiquidation =
    variant === "sl" && stopLossPastLiquidationError(percent, leverage) != null;
  const goesUp = (positionType === "LONG") === (variant === "tp");

  const tone =
    variant === "tp"
      ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
      : "border-red-500/30 bg-red-500/10 text-red-300";

  return (
    <div className="space-y-1.5">
      <div
        className={`rounded-lg border px-2.5 py-1.5 ${
          target != null ? tone : "border-gray-600/40 bg-white/5 text-text-muted"
        }`}
      >
        <p className="text-[10px] uppercase tracking-wide opacity-80">
          Triggers at
        </p>
        <p
          className={`flex items-center gap-1 font-mono text-sm font-semibold tabular-nums ${
            pastLiquidation ? "line-through opacity-60" : ""
          }`}
        >
          {target != null ? (
            <>
              <span aria-hidden className="text-[10px]">
                {goesUp ? "▲" : "▼"}
              </span>
              ${formatTargetPrice(target)}
            </>
          ) : (
            "—"
          )}
        </p>
      </div>
      {liquidation != null && (
        <p
          className={`px-0.5 text-[10px] ${
            pastLiquidation ? "font-semibold text-red-400" : "text-text-muted"
          }`}
        >
          Liq. ≈{" "}
          <span className="font-mono tabular-nums">
            ${formatTargetPrice(liquidation)}
          </span>
        </p>
      )}
    </div>
  );
};
